/**
 * Australian Business Number (ABN) validation and formatting.
 * ABN checksum algorithm: https://abr.business.gov.au/Help/AbnFormat
 */

const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19] as const

/**
 * Validate an ABN using the official checksum algorithm.
 * @param abn - ABN string (spaces allowed)
 * @returns true if valid, false otherwise
 */
export function validateAbn(abn: string): boolean {
  const digits = abn.replace(/\s+/g, "")
  if (!/^\d{11}$/.test(digits)) return false

  const digitArray = digits.split("").map((d) => parseInt(d, 10))
  // Subtract 1 from the first digit
  digitArray[0]! -= 1

  // Sum weighted digits
  const sum = digitArray.reduce((acc, digit, index) => acc + digit * ABN_WEIGHTS[index]!, 0)

  return sum % 89 === 0
}

/**
 * Format an ABN as "XX XXX XXX XXX".
 * @param abn - ABN string (with or without spaces)
 * @returns Formatted ABN or original string if invalid length
 */
export function formatAbn(abn: string): string {
  const digits = abn.replace(/\s+/g, "")
  if (digits.length !== 11) return abn
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`
}

/**
 * Strip formatting from an ABN, returning only digits.
 */
export function stripAbn(abn: string): string {
  return abn.replace(/\s+/g, "")
}

/**
 * Validate an ACN (Australian Company Number) — 9 digits with mod-89 checksum.
 * Note: ACN uses a different algorithm than ABN.
 */
export function validateAcn(acn: string): boolean {
  const digits = acn.replace(/\s+/g, "")
  if (!/^\d{9}$/.test(digits)) return false

  const weights = [8, 7, 6, 5, 4, 3, 2, 1]
  const digitArray = digits.split("").map((d) => parseInt(d, 10))
  const sum = weights.reduce((acc, w, i) => acc + w * digitArray[i]!, 0)
  const remainder = sum % 10
  const checkDigit = remainder === 0 ? 0 : 10 - remainder
  return checkDigit === digitArray[8]
}
